import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import {
  CalendarDaysIcon,
  MapPinIcon,
  ClockIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Event } from "../../types";
import { EVENT_DATA } from "../../constants/eventData";

const AdminEventsPage: React.FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();

  // Use hardcoded event data instead of API call
  const event = EVENT_DATA;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Event>();

  const updateEventMutation = useMutation({
    mutationFn: (data: Partial<Event>) => {
      // Since there's only one event, we don't need to update it via API
      // This would be handled by updating the database directly
      throw new Error(
        "Event updates should be done directly in the database for single-event setup"
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event"] });
      setIsEditing(false);
    },
  });

  const handleEdit = () => {
    if (event) {
      reset(event);
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset();
  };

  const onSubmit = (data: Event) => {
    updateEventMutation.mutate(data);
  };

  // No loading or error handling needed since we're using hardcoded data

  return (
    <div className="min-h-screen bg-gray-50 admin-dashboard">
      {/* Header */}
      <motion.header
        className="bg-white shadow-sm border-b border-gray-200"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Event Management
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage your single event details
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {!isEditing ? (
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-900 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PencilIcon className="w-4 h-4 mr-2" />
                  Edit Event
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleCancel}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <XMarkIcon className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit(onSubmit)}
                    disabled={updateEventMutation.isPending}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    <CheckIcon className="w-4 h-4 mr-2" />
                    {updateEventMutation.isPending ? "Saving..." : "Save"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="bg-white rounded-lg shadow-sm border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="p-6">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Event Details */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Title
                  </label>
                  {isEditing ? (
                    <input
                      {...register("title", { required: "Title is required" })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter event title"
                    />
                  ) : (
                    <p className="text-lg font-semibold text-gray-900">
                      {event.title}
                    </p>
                  )}
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.title.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  {isEditing ? (
                    <textarea
                      {...register("description", {
                        required: "Description is required",
                      })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter event description"
                    />
                  ) : (
                    <p className="text-gray-700 leading-relaxed">
                      {event.description}
                    </p>
                  )}
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.description.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Theme
                  </label>
                  {isEditing ? (
                    <input
                      {...register("theme", { required: "Theme is required" })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter event theme"
                    />
                  ) : (
                    <p className="text-gray-700">{event.theme}</p>
                  )}
                  {errors.theme && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.theme.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Event Info */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Date
                  </label>
                  {isEditing ? (
                    <input
                      {...register("date", { required: "Date is required" })}
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
                      <p className="text-gray-700">
                        {new Date(event.date).toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                  )}
                  {errors.date && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.date.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Time
                  </label>
                  {isEditing ? (
                    <input
                      {...register("time", { required: "Time is required" })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., 6:00 PM - 9:00 PM"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="w-5 h-5 text-gray-400" />
                      <p className="text-gray-700">{event.time}</p>
                    </div>
                  )}
                  {errors.time && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.time.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  {isEditing ? (
                    <input
                      {...register("location", {
                        required: "Location is required",
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter event location"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <MapPinIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-gray-700">{event.location}</p>
                        <p className="text-sm text-gray-500">{event.address}</p>
                      </div>
                    </div>
                  )}
                  {errors.location && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.location.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  {isEditing ? (
                    <input
                      {...register("address", {
                        required: "Address is required",
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter full address"
                    />
                  ) : (
                    <p className="text-gray-700">{event.address}</p>
                  )}
                  {errors.address && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.address.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image URL
                  </label>
                  {isEditing ? (
                    <input
                      {...register("imageUrl")}
                      type="url"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter image URL"
                    />
                  ) : (
                    <p className="text-gray-700 break-all">
                      {event.imageUrl || "No image set"}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Event Image Preview */}
            {event.imageUrl && (
              <div className="mt-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Image Preview
                </label>
                <div className="relative rounded-lg overflow-hidden shadow-sm border border-gray-200">
                  <img
                    src={event.imageUrl}
                    alt={event.title}
                    className="w-full h-64 object-cover"
                  />
                </div>
              </div>
            )}

            {/* Info Notice */}
            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-blue-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Single Event Setup
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      This platform is designed for a single event. To update
                      event details, modify the data directly in your database
                      or update the initialization script.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default AdminEventsPage;
